import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { QuestionnaireType } from 'src/API';
import {
  DefaultTemplateConfig,
  DaySheetTemplateService
} from 'src/app/day-sheets/day-sheet-template.service';
import { DaySheetTemplate } from 'src/app/day-sheets/day-sheet.types';
import { SymptomModes } from 'src/app/shared/symptoms/symptom.model';
import { DaySheetTemplateFormDialogComponent } from './day-sheet-template-form-dialog.component';

@Component({
  selector: 'csi-day-sheet-templates',
  templateUrl: './day-sheet-templates.component.html',
  styleUrls: ['./day-sheet-templates.component.scss']
})
export class DaySheetTemplatesComponent implements OnInit, OnDestroy {
  public templates: DaySheetTemplate[] = [];
  public isLoading = false;

  public readonly defaultTemplates: DefaultTemplateConfig[];
  public readonly defaultColumns = ['name', 'signs', 'questionnaires', 'assessment'];
  public readonly customColumns = ['name', 'signs', 'questionnaires', 'assessment', 'actions'];

  public readonly assessmentTypeLabels: Record<SymptomModes, string> = {
    [SymptomModes.PreOpScreening]: 'Pre-Operative Screening',
    [SymptomModes.PreOp]: 'ASCRS Pre-Op',
    [SymptomModes.Simple]: 'Screening',
    [SymptomModes.Silver]: 'Accelerated Dry Eye Assessment',
    [SymptomModes.Advanced]: 'Gold Standard',
    [SymptomModes.Treatment]: 'Treatment',
    [SymptomModes.FollowUp]: 'Follow Up',
    [SymptomModes.DewsIII]: 'DEWS III'
  };

  public readonly questionnaireLabels: Partial<Record<QuestionnaireType, string>> = {
    [QuestionnaireType.DEQ]: 'DEQ',
    [QuestionnaireType.SPEED]: 'SPEED',
    [QuestionnaireType.SPEEDII]: 'SPEED II',
    [QuestionnaireType.OSDI]: 'OSDI',
    [QuestionnaireType.OSDI6]: 'OSDI-6',
    [QuestionnaireType.OSDIAndSPEED]: 'OSDI + SPEED',
    [QuestionnaireType.FITZPATRICK]: 'Fitzpatrick',
    [QuestionnaireType.MEDICATION]: 'Medications',
    [QuestionnaireType.DEWSIII_Q]: 'DEWS III Questionnaire'
  };

  constructor(
    private templateService: DaySheetTemplateService,
    private dialog: MatDialog,
    private translocoService: TranslocoService
  ) {
    this.defaultTemplates = this.templateService.defaultTemplateConfigs;
  }

  ngOnInit(): void {
    this.loadTemplates();
  }

  ngOnDestroy(): void {}

  public get customTemplates(): DaySheetTemplate[] {
    return this.templates.filter(t => !this.isDefault(t));
  }

  public loadTemplates(): void {
    this.isLoading = true;
    this.templateService
      .listTemplates()
      .pipe(untilDestroyed(this))
      .subscribe(
        templates => {
          this.templates = templates;
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        }
      );
  }

  public openCreateDialog(): void {
    const ref = this.dialog.open(DaySheetTemplateFormDialogComponent, {
      data: null,
      width: '560px',
      maxWidth: '95vw',
      disableClose: true
    });
    ref
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(result => {
        if (result) {
          this.loadTemplates();
        }
      });
  }

  public openEditDialog(template: DaySheetTemplate): void {
    const ref = this.dialog.open(DaySheetTemplateFormDialogComponent, {
      data: template,
      width: '560px',
      maxWidth: '95vw',
      disableClose: true
    });
    ref
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(result => {
        if (result) {
          this.loadTemplates();
        }
      });
  }

  public deleteTemplate(template: DaySheetTemplate): void {
    if (!template.id) {
      return;
    }
    this.templateService
      .deleteTemplate(template.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.templates = this.templates.filter(t => t.id !== template.id);
      });
  }

  public getAssessmentLabel(assessmentType: string | null | undefined): string {
    if (!assessmentType) {
      return '—';
    }
    const key = this.assessmentTypeLabels[assessmentType as SymptomModes] || assessmentType;
    return this.translocoService.translate(key);
  }

  public getQuestionnairesSummary(types: string[]): string {
    if (!types.length) {
      return '—';
    }
    return types
      .map(t => {
        const key = this.questionnaireLabels[t as QuestionnaireType] || t;
        return this.translocoService.translate(key);
      })
      .join(', ');
  }

  public getDbSignCount(template: DaySheetTemplate): number {
    return this.templateService.getTemplateSigns(template).length;
  }

  public getDbQuestionnaires(template: DaySheetTemplate): string {
    return this.getQuestionnairesSummary(this.templateService.parseQuestionnaires(template));
  }

  private isDefault(template: DaySheetTemplate): boolean {
    return !!template.id && this.templateService.defaultTemplateIds.has(template.id);
  }
}
